import { Handshake } from 'lucide-react';
import { CARD_REPLIES, meetingKindLabel, pickReply } from '../../lib/scout-meeting-card';
import { formatCalendarDate } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { getExpenseCategoryLabel } from '../../lib/expenses';
import type { ScoutMeetingData } from '../../types/scout';

/**
 * Scout's meeting-log card (api#1078): the draft as ONE card the rep can read
 * at a glance and answer with one tap, rendered from the API's `meeting` data
 * (api#1089; lib/scout-meeting-card.ts).
 *
 * The buttons send the rep's own reply — "Yes", "No", a numbered pick, who
 * paid — exactly as if they had typed or said it, because the API's confirm
 * gate only accepts a yes that arrives in the USER's turn. The card never
 * confirms anything itself.
 *
 * Dates are CALENDAR days in the tenant's zone (`occurred_on`,
 * `follow_up_date`), formatted as days and never turned into instants. Money
 * is the API's amount, formatted and nothing else. A field the API sent as
 * null is `—`; a known nothing (no contact named, no expense) says so.
 *
 * `active` is false once a later turn exists or a turn is running: an old
 * card's buttons would answer a question nobody is asking any more.
 */

/** Badges: saturated fill, dark ink. Contact state is a boolean, so this is exhaustive. */
const CONTACT_BADGE = {
  on_file: { label: 'On file', fill: 'bg-fi-signal-green' },
  new: { label: 'New contact', fill: 'bg-fi-signal-amber' },
} as const;

/**
 * Exhaustive over paid_by. `null` on a DRAFT is a payer the API did not
 * resolve: slate and `—`, never a guess — who paid decides whether someone is
 * reimbursed.
 */
const PAID_BADGE = {
  company: { label: 'Company card', fill: 'bg-fi-signal-blue' },
  personal: { label: 'You paid · reimbursed', fill: 'bg-fi-signal-amber' },
  unknown: { label: '—', fill: 'bg-fi-signal-slate' },
} as const;

function Badge({ label, fill }: { label: string; fill: string }) {
  return (
    <span className={`inline-flex w-fit self-start items-center rounded-fi-pill border border-black/20 px-2 py-0.5 text-[0.65rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${fill}`}>
      {label}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    // Stacked on a phone, where a label column leaves the value ~150px.
    <div data-testid={`card-row-${label}`} className="grid grid-cols-1 gap-0.5 py-1.5 text-sm sm:grid-cols-[96px_1fr] sm:gap-2">
      <dt className="text-xs text-fieldiq-muted sm:text-sm">{label}</dt>
      <dd className="min-w-0 break-words text-fieldiq-text">{children}</dd>
    </div>
  );
}

const PRIMARY = 'flex-1 min-h-[48px] rounded-xl bg-fieldiq-accent text-black text-sm font-bold active:opacity-90 disabled:opacity-50';
const SECONDARY = 'flex-1 min-h-[48px] rounded-xl bg-fieldiq-card border border-fieldiq-border text-fieldiq-text text-sm font-semibold active:bg-fieldiq-border/50 disabled:opacity-50';

const day = (v: string | null | undefined) => formatCalendarDate(v) ?? '—';

/** Absent key → `—` (not sent); [] → nobody else (sent, and empty). */
function attendeesText(a: string[] | null | undefined): string {
  if (!Array.isArray(a)) return '—';
  return a.length ? a.join(', ') : 'Nobody else named';
}

function DraftBody({ m }: { m: ScoutMeetingData }) {
  const paid = m.expense ? PAID_BADGE[m.expense.paid_by ?? 'unknown'] : null;
  const category = m.expense?.category ? getExpenseCategoryLabel(m.expense.category) : null;
  return (
    <dl className="divide-y divide-fieldiq-border">
      <Row label="Customer">{m.customer?.name ?? '—'}</Row>
      <Row label="Activity">{`${meetingKindLabel(m.kind)} · ${day(m.occurred_on)}`}</Row>
      <Row label="Contact">
        {m.contact ? (
          <span className="flex flex-wrap items-center gap-2">
            <span>{m.contact.name || '—'}</span>
            <Badge {...CONTACT_BADGE[m.contact.is_new ? 'new' : 'on_file']} />
          </span>
        ) : 'None named'}
      </Row>
      <Row label="Attendees">{attendeesText(m.attendees)}</Row>
      <Row label="Notes">{m.notes ?? '—'}</Row>
      {m.next_step && <Row label="Next step">{m.next_step}</Row>}
      {m.follow_up_date && <Row label="Follow-up">{day(m.follow_up_date)}</Row>}
      <Row label="Expense">
        {m.expense && paid ? (
          <span className="flex flex-col gap-1">
            <Badge {...paid} />
            <span className="tabular-nums">{fmtCurrency(m.expense.amount)}{category ? ` · ${category}` : ''}</span>
          </span>
        ) : 'None'}
      </Row>
    </dl>
  );
}

interface Props {
  card: ScoutMeetingData;
  active: boolean;
  onReply: (text: string) => void;
}

export default function ScoutMeetingCard({ card, active, onReply }: Props) {
  const isDraft = card.state === 'draft';
  const question = card.state === 'pick_customer' ? 'Which customer did you mean?'
    : card.state === 'pick_contact' ? (card.customer?.name ? `Which of ${card.customer.name}'s contacts?` : 'Which contact?')
      : card.state === 'ask_paid_by' ? `Who paid the ${fmtCurrency(card.expense?.amount)}?`
        : null;

  return (
    <section
      data-testid={`scout-card-${card.state}`}
      aria-label={isDraft ? 'Meeting to log' : 'Scout needs an answer'}
      className="mt-2 w-full max-w-[520px] rounded-xl border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      <div className="flex items-center gap-2 px-3 pt-3">
        <Handshake size={16} aria-hidden className="text-fieldiq-muted" />
        <p className="text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted">
          {isDraft ? 'Meeting to log — not saved yet' : 'Scout needs one answer'}
        </p>
      </div>

      <div className="px-3 pb-3">
        {isDraft && <DraftBody m={card} />}

        {question && <p className="pt-2 text-sm font-semibold text-white">{question}</p>}

        {(card.state === 'pick_customer' || card.state === 'pick_contact') && (
          <ol className="mt-2 space-y-2">
            {(card.candidates ?? []).map((c) => (
              <li key={`${c.n}:${c.id}`}>
                {active ? (
                  <button type="button" onClick={() => onReply(pickReply(c))}
                    className="w-full min-h-[48px] rounded-xl border border-fieldiq-border bg-fieldiq-dark px-3 text-left text-sm text-fieldiq-text hover:border-fieldiq-accent">
                    <span className="mr-2 font-bold tabular-nums">{c.n}.</span>{c.name}
                    {c.detail && <span className="ml-1 text-fieldiq-muted">— {c.detail}</span>}
                  </button>
                ) : (
                  <p className="px-3 py-1 text-sm text-fieldiq-muted">
                    <span className="mr-2 font-bold tabular-nums">{c.n}.</span>{c.name}{c.detail ? ` — ${c.detail}` : ''}
                  </p>
                )}
              </li>
            ))}
          </ol>
        )}

        {card.state === 'ask_paid_by' && active && (
          <div className="mt-3 flex gap-2">
            <button type="button" className={SECONDARY} onClick={() => onReply(CARD_REPLIES.personal)}>I paid</button>
            <button type="button" className={SECONDARY} onClick={() => onReply(CARD_REPLIES.company)}>Company card</button>
          </div>
        )}

        {isDraft && active && (
          <>
            <div className="mt-3 flex gap-2">
              <button type="button" className={PRIMARY} onClick={() => onReply(CARD_REPLIES.yes)}>Yes, save it</button>
              <button type="button" className={SECONDARY} onClick={() => onReply(CARD_REPLIES.no)}>No</button>
            </div>
            <p className="mt-2 text-xs text-fieldiq-muted">Nothing is saved until you say yes. Nothing is sent to anyone.</p>
          </>
        )}
      </div>
    </section>
  );
}
